import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  SelectChangeEvent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Autocomplete,
} from '@mui/material';
import { groupPlayAPI, memberProfileAPI } from '../../services/api';
import { GroupPlay, GroupPlayInstance, MemberProfile } from '../../types';
import { formatDate, formatTimeString } from '../../utils/dateUtils';

interface BookGroupPlayDialogProps {
  open: boolean;
  onClose: () => void;
  groupPlay: GroupPlay;
  instance: GroupPlayInstance | null;
  onSuccess: (message?: string) => void;
}

const BookGroupPlayDialog: React.FC<BookGroupPlayDialogProps> = ({
  open,
  onClose,
  groupPlay,
  instance,
  onSuccess,
}) => {
  const [members, setMembers] = useState<MemberProfile[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');
  const [isGuest, setIsGuest] = useState<boolean>(false);
  const [guestName, setGuestName] = useState<string>('');
  const [guestEmail, setGuestEmail] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingMembers, setLoadingMembers] = useState<boolean>(true);
  
  // Multi-instance booking states
  const [availableInstances, setAvailableInstances] = useState<GroupPlayInstance[]>([]);
  const [selectedInstanceIds, setSelectedInstanceIds] = useState<string[]>([]);
  const [loadingInstances, setLoadingInstances] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      fetchMembers();
      // Reset form
      setSelectedMemberId('');
      setIsGuest(false);
      setGuestName('');
      setGuestEmail('');
      setNotes('');
      setError(null);
      setSelectedInstanceIds([]);
      
      // If no specific instance provided, fetch instances
      if (!instance) {
        fetchAvailableInstances();
      } else {
        // If a specific instance is provided, pre-select it
        setSelectedInstanceIds([instance.id]);
      }
    }
  }, [open, groupPlay, instance]);

  const fetchMembers = async () => {
    try {
      setLoadingMembers(true);
      const response = await memberProfileAPI.getAllMemberProfiles();
      // Filter to active members only
      const activeMembers = response.data.filter((m: MemberProfile) => m.status === 'active');
      setMembers(activeMembers);
    } catch (error: any) {
      console.error('Error fetching members:', error);
      setError('Failed to load members');
    } finally {
      setLoadingMembers(false);
    }
  };

  const fetchAvailableInstances = async () => {
    try {
      setLoadingInstances(true);
      const today = new Date().toISOString().split('T')[0];
      const response = await groupPlayAPI.getGroupPlayInstances(groupPlay.id, {
        startDate: today,
      });
      // Filter to upcoming instances only
      const upcomingInstances = response.data.filter((inst: GroupPlayInstance) => 
        inst.status === 'SCHEDULED' && inst.sessionDate >= today
      );
      setAvailableInstances(upcomingInstances);
      
      // For non-recurring group plays, auto-select the single instance
      if (!groupPlay.recurringPattern && upcomingInstances.length === 1) {
        setSelectedInstanceIds([upcomingInstances[0].id]);
      }
    } catch (error: any) {
      console.error('Error fetching instances:', error);
      setError('Failed to load available sessions');
    } finally {
      setLoadingInstances(false);
    }
  };

  const handleToggleInstance = (instanceId: string) => {
    setSelectedInstanceIds(prev => {
      if (prev.includes(instanceId)) {
        return prev.filter(id => id !== instanceId);
      } else {
        return [...prev, instanceId];
      }
    });
  };

  const handleSubmit = async () => {
    setError(null);

    // Validation
    if (!isGuest && !selectedMemberId) {
      setError('Please select a member');
      return;
    }

    if (isGuest) {
      if (!guestName.trim()) {
        setError('Guest name is required');
        return;
      }
      if (!guestEmail.trim()) {
        setError('Guest email is required');
        return;
      }
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(guestEmail)) {
        setError('Please enter a valid email address');
        return;
      }
      if (!groupPlay.allowGuestBookings) {
        setError('Guest bookings are not allowed for this group play');
        return;
      }
    }

    // Validate instance selection
    if (selectedInstanceIds.length === 0) {
      if (groupPlay.recurringPattern) {
        setError('Please select at least one session to book');
      } else {
        setError('No available sessions to book');
      }
      return;
    }

    try {
      setLoading(true);

      const bookingData: any = {
        notes: notes.trim() || undefined,
      };

      if (isGuest) {
        bookingData.isGuest = true;
        bookingData.guestName = guestName.trim();
        bookingData.guestEmail = guestEmail.trim();
      } else {
        bookingData.memberProfileId = selectedMemberId;
      }

      // Book each selected instance
      const bookingPromises = selectedInstanceIds.map(instanceId =>
        groupPlayAPI.bookGroupPlayInstance(groupPlay.id, instanceId, bookingData)
      );

      await Promise.all(bookingPromises);

      const memberName = isGuest 
        ? guestName 
        : `${members.find(m => m.id === selectedMemberId)?.firstName} ${members.find(m => m.id === selectedMemberId)?.lastName}`;

      onSuccess(
        selectedInstanceIds.length > 1
          ? `Successfully booked ${memberName} for ${selectedInstanceIds.length} sessions`
          : `Successfully booked ${memberName}`
      );
      onClose();
    } catch (error: any) {
      console.error('Error booking group play:', error);
      setError(
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to book group play'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Book Group Play: {groupPlay.name}
        {instance && !groupPlay.recurringPattern && (
          <Typography variant="body2" color="text.secondary">
            Session: {formatDate(instance.sessionDate)} at {formatTimeString(instance.startTime)}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {groupPlay.allowGuestBookings && (
          <FormControlLabel
            control={
              <Checkbox
                checked={isGuest}
                onChange={(e) => {
                  setIsGuest(e.target.checked);
                  setError(null);
                }}
              />
            }
            label="Book as Guest"
            sx={{ mb: 2 }}
          />
        )}

        {isGuest ? (
          <>
            <TextField
              fullWidth
              label="Guest Name"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Guest Email"
              type="email"
              value={guestEmail}
              onChange={(e) => setGuestEmail(e.target.value)}
              required
              sx={{ mb: 2 }}
            />
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Guest Fee: ${groupPlay.guestSessionFee || 0} per session
              </Typography>
            </Box>
          </>
        ) : (
          <>
            <Autocomplete
              options={members}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
              value={members.find(member => member.id === selectedMemberId) || null}
              onChange={(event, newValue) => {
                setSelectedMemberId(newValue?.id || '');
                setError(null);
              }}
              loading={loadingMembers}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Member"
                  required
                  placeholder="Search for a member..."
                  sx={{ mb: 2 }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingMembers ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(options, { inputValue }) => {
                const filtered = options.filter(option =>
                  option.firstName.toLowerCase().includes(inputValue.toLowerCase()) ||
                  option.lastName.toLowerCase().includes(inputValue.toLowerCase()) ||
                  option.email.toLowerCase().includes(inputValue.toLowerCase())
                );
                return filtered.sort((a, b) => {
                  const firstNameCompare = a.firstName.localeCompare(b.firstName);
                  if (firstNameCompare !== 0) return firstNameCompare;
                  return a.lastName.localeCompare(b.lastName);
                });
              }}
              noOptionsText="No members found"
            />
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Member Fee: ${groupPlay.memberSessionFee} per session
              </Typography>
            </Box>
          </>
        )}

        {/* Instance selection for recurring group plays */}
        {!instance && groupPlay.recurringPattern && (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Select Sessions to Book
            </Typography>
            {loadingInstances ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : availableInstances.length > 0 ? (
              <List sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                {availableInstances.map((inst) => (
                  <ListItem key={inst.id} disablePadding>
                    <ListItemButton
                      onClick={() => handleToggleInstance(inst.id)}
                      selected={selectedInstanceIds.includes(inst.id)}
                    >
                      <Checkbox
                        checked={selectedInstanceIds.includes(inst.id)}
                        tabIndex={-1}
                        disableRipple
                      />
                      <ListItemText
                        primary={`${formatDate(inst.sessionDate)} at ${formatTimeString(inst.startTime)}`}
                        secondary={
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            <Chip
                              label={`${inst.confirmedCount || 0}/${inst.capacity} booked`}
                              size="small"
                              color={
                                (inst.confirmedCount || 0) >= inst.capacity
                                  ? 'error'
                                  : (inst.confirmedCount || 0) >= inst.capacity * 0.8
                                  ? 'warning'
                                  : 'success'
                              }
                            />
                            <Chip label={inst.status} size="small" variant="outlined" />
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Alert severity="info">No upcoming sessions available</Alert>
            )}
            {selectedInstanceIds.length > 0 && (
              <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                {selectedInstanceIds.length} session(s) selected
              </Typography>
            )}
          </Box>
        )}

        <TextField
          fullWidth
          label="Notes (Optional)"
          multiline
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={{ mt: 2 }}
        />

        {selectedInstanceIds.length > 0 && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Booking Summary
            </Typography>
            <Typography variant="body2">
              Sessions: {selectedInstanceIds.length}
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              Total Fee: ${((isGuest ? (groupPlay.guestSessionFee || 0) : groupPlay.memberSessionFee) * selectedInstanceIds.length).toFixed(2)}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || loadingMembers || (groupPlay.recurringPattern && selectedInstanceIds.length === 0)}
        >
          {loading ? <CircularProgress size={24} /> : 'Book'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BookGroupPlayDialog;
